import { RydMixer } from "../audio/mixer";
import { RydBookmark } from "../model/bookmark";
import { ChordContainer, HorizontalNoteContainer } from "../model/containers";
import { LoopingContainer } from "../model/loop";
import { findLargestPPQ, ProjectUIState, RydProject } from "../model/project";
import { MixerStore } from "../stores/MixerStore";


function getInitialLength(line: RydProject): number {
    return line.getLength() > 0 ? line.getLength() : 64;
}

export function addContext(
    project: RydProject,
    setProject: (p: RydProject) => void,
    setWorkspaceUIState: (s: Partial<ProjectUIState>) => void
) {
    project.put(
        project.size(),
        new HorizontalNoteContainer({
            length: getInitialLength(project),
            ppq: findLargestPPQ(project)
        })
    );
    setWorkspaceUIState({ ...project.uiState });
    setProject(project);
}

export function addEmptyHNC(
    project: RydProject,
    setProject: (p: RydProject) => void,
    mixerStore: MixerStore,
    setWorkspaceUIState: (s: Partial<ProjectUIState>) => void
) {
    const emptyHNC = new HorizontalNoteContainer({
        length: getInitialLength(project),
        ppq: findLargestPPQ(project)
    });

    addHNC(project, emptyHNC, setProject, mixerStore, setWorkspaceUIState);
}

export function addHNC(
    project: RydProject,
    hnc: HorizontalNoteContainer,
    setProject: (p: RydProject) => void,
    mixerStore: MixerStore,
    setWorkspaceUIState: (s: Partial<ProjectUIState>) => void,
    name?: string
) {
    const trackId = project.size();
    project.put(
        trackId,
        hnc,
        { skipLengthCheck: true },
        { name: name || `track-${trackId}` }
    );
    const t: RydMixer.TrackParams = {
        id: trackId,
        name: name || `track-${project.size()}`,
        gain: 1.0,
        pan: 0.0,
        muted: false,
        solo: false,
        onBus: true
    };
    project.mixerState.tracks.set(trackId, t);

    // FIXME: let this happen inside CurrentProject.
    mixerStore.mixer?.addTrack(t);
    setWorkspaceUIState({ ...project.uiState });

    setProject(project);
}

export function addChordLine(
    project: RydProject,
    setProject: (p: RydProject) => void,
    setWorkspaceUIState: (s: Partial<ProjectUIState>) => void
) {
    project.put(project.size(),
        new ChordContainer({
            length: getInitialLength(project),
            ppq: findLargestPPQ(project)
        }));
    setWorkspaceUIState({ ...project.uiState });
    setProject(project);
}

export function addLoopTrack(
    project: RydProject,
    hnc: HorizontalNoteContainer,
    setProject: (p: RydProject) => void,
    mixerStore: MixerStore,
    setWorkspaceUIState: (s: Partial<ProjectUIState>) => void
) {
    const loopStart = {
        scope: 'track',
        position: 0,
        name: 'start',
        color: 'brown',
        id: 'loop-start',
        note: 'Loop start'
    } as RydBookmark;

    const loopEnd = {
        scope: 'track',
        position: hnc.getLength(),
        name: 'end',
        color: 'brown',
        id: 'loop-end',
        note: 'Loop end'
    } as RydBookmark;

    const loop = new LoopingContainer({
        baseContainer: hnc,
        loopStart: loopStart,
        loopEnd: loopEnd,
        loopLimit: 'inf',
    });

    addHNC(
        project,
        loop.getBaseContainer() as HorizontalNoteContainer,
        setProject,
        mixerStore,
        setWorkspaceUIState,
        `loop-${project.size()}`
    );
    project.tracks.get(project.size() - 1)?.bookmarks
        .push(
            { ...loop.getLoopStart(), selected: false },
            { ...loop.getLoopEnd(), selected: false }
        );
    setProject(project);
}